/**
 * The e-mail templates of one owner: every notice in Vietnamese and English, where each comes
 * from (this brand, the platform, built in), an editor with a live preview in the brand's
 * layout, and "back to default".
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '../../api';
import type { EmailTemplate, TemplateInput } from '../../api/areas/notify';
import { Badge } from '../../components/Badge';
import { Callout, Field } from '../../components/blocks';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useApi } from '../../lib/useApi';
import { Panel } from '../integrations/shared';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

/** These texts show template syntax (`{{ domain }}`): not i18next placeholders. */
const RAW = { interpolation: { prefix: '[[[', suffix: ']]]' } } as const;

function Editor({ owner, template, onClose, onChanged }: { owner: string; template: EmailTemplate; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation('integrations');
  const tr = t as unknown as Lookup;
  const [draft, setDraft] = useState<TemplateInput>({ subject: template.subject, body_html: template.body_html, body_text: template.body_text });
  const [previewOf, setPreviewOf] = useState<TemplateInput>(draft);
  const layout = template.key === 'layout';
  const preview = useApi(
    (signal) => api.previewEmailTemplate(owner, { key: template.key, locale: template.locale, draft: previewOf }, { signal }),
    [owner, template.key, template.locale, previewOf],
  );
  const save = useWrite((b: TemplateInput, opts) => api.saveEmailTemplate(owner, template.key, template.locale, b, opts));
  const revert = useWrite((_: null, opts) => api.revertEmailTemplate(owner, template.key, template.locale, opts));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="grid content-start gap-3">
        {!layout ? (
          <Field label={t('email.tpl.subject')}>
            <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          </Field>
        ) : null}
        <Field label={t('email.tpl.html')} hint={layout ? tr('email.tpl.layoutHint', RAW) : t('email.tpl.htmlHint')}>
          <Textarea className="min-h-56 font-mono text-xs" value={draft.body_html} onChange={(e) => setDraft({ ...draft, body_html: e.target.value })} />
        </Field>
        <Field label={t('email.tpl.text')}>
          <Textarea className="min-h-32 font-mono text-xs" value={draft.body_text} onChange={(e) => setDraft({ ...draft, body_text: e.target.value })} />
        </Field>
        <p className="text-xs text-muted-foreground">{tr('email.tpl.variables', RAW)}</p>
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setPreviewOf(draft)}>
            {t('email.tpl.refreshPreview')}
          </Button>
        </div>
      </div>
      <div className="grid content-start gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t('email.tpl.preview')}</span>
        {preview.status === 'loading' ? <Loading rows={3} /> : null}
        {preview.status === 'error' ? <WriteError error={preview.error} /> : null}
        {preview.status === 'success' ? (
          <>
            <p className="rounded border bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{t('email.tpl.subject')}: </span>
              {preview.data.subject || '—'}
            </p>
            <iframe title={tr('email.tpl.preview')} sandbox="" srcDoc={preview.data.html} className="h-96 w-full rounded border bg-white" />
          </>
        ) : null}
      </div>
      <div className="lg:col-span-2">
        {save.state.phase === 'error' ? <WriteError error={save.state.error} /> : null}
        {revert.state.phase === 'error' ? <WriteError error={revert.state.error} /> : null}
        {save.state.phase === 'done' ? <Callout tone="ok">{t('email.tpl.saved')}</Callout> : null}
        <DialogFooter className="mt-3">
          {template.own ? (
            <Button
              type="button"
              variant="outline"
              disabled={revert.busy}
              onClick={() =>
                void revert.run(null).then((r) => {
                  if (r) {
                    onChanged();
                    onClose();
                  }
                })
              }
            >
              {t('email.tpl.revert')}
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onClose}>
            {t('email.tpl.close')}
          </Button>
          <Button
            type="button"
            disabled={save.busy}
            onClick={() =>
              void save.run(layout ? { ...draft, subject: '-' } : draft).then((r) => {
                if (r) {
                  setPreviewOf(draft);
                  onChanged();
                }
              })
            }
          >
            {t('email.tpl.save')}
          </Button>
        </DialogFooter>
      </div>
    </div>
  );
}

export function TemplatesPanel({ owner }: { owner: string }) {
  const { t } = useTranslation('integrations');
  const tr = t as unknown as Lookup;
  const r = useApi((signal) => api.listEmailTemplates(owner, { signal }), [owner]);
  const [open, setOpen] = useState<EmailTemplate | null>(null);
  const keys = r.status === 'success' ? [...new Set(r.data.items.map((i) => i.key))] : [];
  const sourceTone = (s: EmailTemplate['source']) => (s === 'BUILT_IN' ? 'neutral' : 'info');
  return (
    <Panel title={t('email.tpl.title')} description={owner === 'platform' ? t('email.tpl.descriptionPlatform') : t('email.tpl.descriptionCustomer')} flush>
      {r.status === 'loading' ? (
        <div className="px-5 pb-5">
          <Loading rows={4} />
        </div>
      ) : null}
      {r.status === 'error' ? (
        <div className="px-5 pb-5">
          <WriteError error={r.error} />
        </div>
      ) : null}
      {r.status === 'success' ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="px-5 py-2 font-medium">{t('email.tpl.notice')}</th>
                <th className="px-3 py-2 font-medium">Tiếng Việt</th>
                <th className="px-3 py-2 font-medium">English</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k} className="border-b last:border-0">
                  <td className="px-5 py-2">
                    <div className="font-medium">{tr(`email.keys.${k}`, { defaultValue: k })}</div>
                    <div className="font-mono text-xs text-muted-foreground">{k}</div>
                  </td>
                  {(['vi', 'en'] as const).map((l) => {
                    const item = r.data.items.find((i) => i.key === k && i.locale === l);
                    return (
                      <td key={l} className="px-3 py-2">
                        {item ? (
                          <button type="button" className="inline-flex items-center gap-2 rounded px-1 text-left hover:underline" onClick={() => setOpen(item)}>
                            <Badge tone={sourceTone(item.source)} icon={false}>
                              {t(`email.tpl.sources.${item.source}`)}
                            </Badge>
                            <span className="text-primary">{t('email.tpl.edit')}</span>
                          </button>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[94vh] overflow-y-auto sm:max-w-6xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{open ? `${tr(`email.keys.${open.key}`, { defaultValue: open.key })} · ${open.locale === 'vi' ? 'Tiếng Việt' : 'English'}` : ''}</DialogTitle>
            <DialogDescription>{open ? t(`email.tpl.sourceNote.${open.source}`) : ''}</DialogDescription>
          </DialogHeader>
          {open ? <Editor key={`${open.key}-${open.locale}`} owner={owner} template={open} onClose={() => setOpen(null)} onChanged={r.reload} /> : null}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
